import React, { useMemo, useState } from "react";
import axios from "axios";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useCart } from "layouts/ecommerce/CartContext";
import Nav from "components/Nav";
import Swal from "sweetalert2";
import {
  Container,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import { Add, Remove, Delete, LocalShipping, ShoppingCart, Notes } from "@mui/icons-material";
import BASE_URL from "config/Base_URL";

const API_URL = `${BASE_URL}/add/sales/customer`;

export default function CheckoutPage() {
  const { cart, updateQty, decreaseQty, removeFromCart, clearCart, userId } = useCart();

  const [shippingMethod, setShippingMethod] = useState("");
  const [shippingDate, setShippingDate] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");

  const totalPayment = useMemo(
    () => cart.reduce((sum, item) => sum + (item.price || 0) * (item.qty || 0), 0),
    [cart]
  );

  const formatIDR = (n) =>
    (n || 0).toLocaleString("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    });

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (!cart.length) {
      alert("Keranjang masih kosong.");
      return;
    }

    if (!shippingMethod) {
      alert("Silakan pilih metode pengiriman terlebih dahulu.");
      return;
    }

    if (shippingMethod === "Dikirim ke Alamat" && !address.trim()) {
      alert("Alamat wajib diisi untuk metode diantarkan.");
      return;
    }

    function formatDateLocal(date) {
      return (
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0")
      );
    }

    const today = new Date();
    const todayStr = formatDateLocal(today);

    // const tomorrow = new Date(today);
    // tomorrow.setDate(today.getDate() + 1);
    // const tomorrowStr = formatDateLocal(tomorrow);

    const payload = {
      businessName: userId || "guest",
      date: todayStr,
      // deliveryDate: tomorrowStr,
      deliveryDate: shippingDate,
      deliveryStatus: "PENGIRIMAN",
      totalPayment: 0,
      note: note || "-",
      shippingAddress: shippingMethod === "Dikirim ke Alamat" ? address.trim() : "",
      shippingMethod: shippingMethod,
      items: cart.map((item) => ({
        name: item.name ?? "",
        id: item.id ?? "",
        price: Number(item.price) || 0,
        unit: item.unit ?? "pcs",
        amount: Number(item.qty) || 0,
      })),
    };

    try {
      const token = sessionStorage.getItem("token");
      const res = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      console.log("Checkout success:", res.data);
      Swal.fire("Berhasil", "Berhasil melakukan pemesanan", "success");
      clearCart();
    } catch (err) {
      console.error("Checkout gagal:", err);
      Swal.fire("Gagal", "Gagal melakukan pemesanan", "error");
    }
  };

  return (
    <>
      <Nav />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{ fontWeight: "bold", mb: 3 }}
        >
          Checkout
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 4 }}>
          <b>{userId}</b>
        </Typography>

        <Grid container spacing={4} component="form" onSubmit={handleCheckout}>
          {/* Kolom Kiri - Daftar Barang */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <ShoppingCart sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                  Keranjang Belanja
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {!cart.length ? (
                <Alert severity="info">Keranjang masih kosong.</Alert>
              ) : (
                <List>
                  {cart.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            style={{ marginTop: "15%" }}
                          >
                            {item.unit || "pcs"} · {formatIDR(item.price)}
                          </Typography>
                        }
                      />

                      <ListItemSecondaryAction>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => decreaseQty(item.id)}
                            disabled={item.qty <= 0.01}
                            style={{ backgroundColor: "lightcoral" }}
                          >
                            <Remove />
                          </IconButton>

                          <TextField
                            type="number"
                            value={item.qty}
                            onChange={(e) => {
                              const value = e.target.value;
                              const regex = /^\d*\.?\d{0,2}$/; // maksimal 2 desimal
                              if (value === "" || regex.test(value)) {
                                updateQty(item.id, value === "" ? 0.01 : parseFloat(value));
                              }
                            }}
                            onBlur={() => {
                              if (item.qty < 0.01 || isNaN(item.qty)) {
                                updateQty(item.id, 0.01); // minimal 0.01
                              } else {
                                updateQty(item.id, parseFloat(item.qty.toFixed(2))); // bulatkan 2 desimal
                              }
                            }}
                            inputProps={{
                              min: 0.01,
                              step: 0.01,
                              style: { textAlign: "center", width: 40 },
                            }}
                            variant="outlined"
                            size="small"
                            required
                          />

                          <IconButton
                            size="small"
                            onClick={() => updateQty(item.id, item.qty + 0.01)}
                            style={{ backgroundColor: "lightskyblue" }}
                          >
                            <Add />
                          </IconButton>

                          <Typography
                            variant="body1"
                            sx={{ minWidth: 100, textAlign: "right", mx: 2 }}
                          >
                            {formatIDR((item.price || 0) * (item.qty || 0))}
                          </Typography>

                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeFromCart(item.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>

            <Paper elevation={3} sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Notes sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                  Catatan Pesanan
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Catatan pesanan (opsional)..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                variant="outlined"
              />
            </Paper>
          </Grid>

          {/* Kolom Kanan - Ringkasan & Checkout */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <LocalShipping sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                  Pengiriman
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <MDInput
                style={{ width: "100%", marginBottom: "10px" }}
                type="date"
                value={shippingDate}
                onChange={(e) => setShippingDate(e.target.value)}
              />
              <FormControl fullWidth required>
                <InputLabel id="shipping-method-label">Metode Pengiriman</InputLabel>
                <Select
                  labelId="shipping-method-label"
                  value={shippingMethod}
                  onChange={(e) => setShippingMethod(e.target.value)}
                  style={{ height: "50px" }}
                  label="Metode Pengiriman"
                >
                  <MenuItem value="">
                    <em>Pilih Metode</em>
                  </MenuItem>
                  <MenuItem value="Ambil sendiri">Ambil Sendiri</MenuItem>
                  <MenuItem value="Dikirim ke Alamat">Diantarkan</MenuItem>
                </Select>
              </FormControl>
              {shippingMethod === "Dikirim ke Alamat" && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Alamat Pengiriman"
                  placeholder="Masukkan alamat lengkap..."
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  variant="outlined"
                  sx={{ mt: 2 }}
                  required
                />
              )}
            </Paper>

            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ringkasan Pembayaran
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body1">Subtotal</Typography>
                  <Typography variant="body1">{formatIDR(totalPayment)}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body1">Ongkos Kirim</Typography>
                  <Typography variant="body1">{formatIDR(0)}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" color="primary">
                    {formatIDR(totalPayment)}
                  </Typography>
                </Box>

                <MDButton
                  type="submit"
                  variant="contained"
                  color="info"
                  fullWidth
                  size="large"
                  sx={{ mt: 3, py: 1.5 }}
                  disabled={!cart.length}
                >
                  Bayar & Checkout
                </MDButton>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
